import { Box } from '@zoopla-eng/zds/box';
import type { BreadcrumbsProps } from '@zoopla-eng/zds/breadcrumbs';
import { Breadcrumbs } from '@zoopla-eng/zds/breadcrumbs';
import {
    AdvertProvider,
    AdvertSlotDfp,
    AD_UNIT_ROOTS,
    AD_UNIT_SECTION,
    AD_UNIT_SECTION_NEW,
    ADBRIDG_CLASS,
    AD_UNIT_PAGE_TYPE,
    getPageSectionForAdUnit
} from '@zoopla/adverts/dfp';
import type { HeadProps } from '@zoopla/layout/head';
import { Head } from '@zoopla/layout/head';
import { AdSideBarLayout } from '@zoopla/layout/page';
import { triggerTracking } from '@zoopla/tracking';
import { useRouter } from 'next/router';
import { FC, ReactNode, useState } from 'react';
import {
    advertChildrenStyle,
    advertContainerStyle,
    advertsStyle,
    stickyStyle
} from './HousePricesLayout.styles.css';

export const trackBreadcrumbLinkClick = (
    action: string | null,
    value: number
) => {
    triggerTracking({
        event: 'GAEvent',
        eventCategory: 'breadcrumb',
        eventAction: 'clickthrough',
        eventLabel: action || undefined,
        eventValue: value + 1
    });
};

type Props = {
    head: HeadProps;
    breadcrumbs: BreadcrumbsProps['items'];
    children: ReactNode;
};

export const HousePricesLayout: FC<React.PropsWithChildren<Props>> = ({
    head,
    breadcrumbs,
    children
}) => {
    const [advertProviderContext] = useState({
        targeting: { activity: 'house_prices_search' },
        adBridgPage: AD_UNIT_SECTION.HOUSE_PRICES,
        adBridgPageNew: AD_UNIT_SECTION_NEW.HOUSE_PRICES,
        pageType:
            AD_UNIT_PAGE_TYPE.LISTINGS_SEARCH_RESULT_PAGE /** as agreed with Ad-ops to limit the amount of page types */
    });
    const { route, asPath } = useRouter();

    const adUnitPage = getPageSectionForAdUnit(route);

    const sidebarBreakpoint = 960;

    const breadcrumbsCount = breadcrumbs.length - 1;
    const breadcrumbItems = breadcrumbs.map((bc, idx) => {
        const bci = {
            ...bc,
            onClick: trackBreadcrumbLinkClick
        };

        if (idx === breadcrumbsCount && route !== '/no-results') {
            bci.href = asPath;
        }

        return bci;
    });

    return (
        <AdvertProvider value={advertProviderContext}>
            <Head {...head} />
            <AdSideBarLayout
                main={
                    <Box fw>
                        <Box
                            {...{
                                padding: '$S200 0',
                                display: 'none',

                                '@M100': {
                                    display: 'block'
                                }
                            }}
                        >
                            <Breadcrumbs items={breadcrumbItems} />
                        </Box>
                        <div className={advertContainerStyle}>
                            <div className={advertChildrenStyle}>
                                {children}
                            </div>
                        </div>
                    </Box>
                }
                adverts={
                    <aside className={advertsStyle}>
                        <AdvertSlotDfp
                            position={0}
                            showOnly={['>', sidebarBreakpoint]}
                            sizes={[[300, 16], 'fluid']}
                            adUnitCode={[
                                AD_UNIT_ROOTS.TEXT_LINK,
                                AD_UNIT_SECTION.HOUSE_PRICES,
                                adUnitPage
                            ]}
                            adbridgClass={ADBRIDG_CLASS.TEXT_LINK}
                            adbridgDataClass={ADBRIDG_CLASS.ADB_TEXT_LINK}
                        />
                        <AdvertSlotDfp
                            position={1}
                            showOnly={['>', sidebarBreakpoint]}
                            sizes={[[300, 250]]}
                            adUnitCode={[
                                AD_UNIT_ROOTS.MPU,
                                AD_UNIT_SECTION.HOUSE_PRICES,
                                adUnitPage
                            ]}
                            adbridgClass={ADBRIDG_CLASS.MPU}
                        />
                        <div className={stickyStyle}>
                            <AdvertSlotDfp
                                position={2}
                                showOnly={['>', sidebarBreakpoint]}
                                sizes={[[300, 600]]}
                                adUnitCode={[
                                    AD_UNIT_ROOTS.SKY_SCRAPER,
                                    AD_UNIT_SECTION.HOUSE_PRICES,
                                    adUnitPage
                                ]}
                                adbridgClass={ADBRIDG_CLASS.SKYSCRAPER}
                            />
                        </div>
                    </aside>
                }
            />
        </AdvertProvider>
    );
};

export default HousePricesLayout;
